import apiConfig from 'constants/apiConfig';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  NotificationPriority,
  NotificationWithMetadataDto,
} from './useNotification';
import { toast } from 'react-toastify';

export const useNotificationStream = () => {
  const [notifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);
  const queryClientRef = useRef(queryClient);
  const groupGuidRef = useRef(groupGuid);
  queryClientRef.current = queryClient;
  groupGuidRef.current = groupGuid;

  useEffect(() => {
    const accessToken = sessionStorage['accessToken'];
    if (!accessToken) return;

    let abortController = new AbortController();
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let isCleaningUp = false;
    const MAX_RECONNECT_ATTEMPTS = 3;

    function scheduleReconnect() {
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log('[SSE] Max reconnection attempts reached');
        return;
      }

      reconnectAttempts++;
      const delay = reconnectAttempts * 5000; // 5s, 10s, 15s
      console.log(
        `[SSE] Reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`
      );

      reconnectTimeout = setTimeout(() => {
        if (isCleaningUp) return;
        abortController = new AbortController();
        connectSSE();
      }, delay);
    }

    async function connectSSE() {
      if (isCleaningUp) return;

      const currentToken = sessionStorage['accessToken'];
      if (!currentToken) {
        console.log('[SSE] No access token available');
        return;
      }

      try {
        console.log('[SSE] Connecting...');

        // Determine if running in Teams context based on sessionStorage.
        // Note: the axios interceptor also considers auth.teamsInitialized.
        const isTeams = sessionStorage['isUsingTeams'] === 'true';
        const teamsHeaderValue = isTeams ? 'true' : 'false';

        const response = await fetch(apiConfig.apiGateway.notificationStream, {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'x-teamsapp': teamsHeaderValue,
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        console.log('[SSE] Connected');
        setIsConnected(true);
        reconnectAttempts = 0; // Reset on successful connection

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('[SSE] Stream closed by server');
            setIsConnected(false);
            scheduleReconnect();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim() || event.trim().startsWith(':')) continue;

            const lines = event.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonString = line.slice(6).trim();
                  const data = JSON.parse(jsonString) as {
                    type: string;
                    notification?: NotificationWithMetadataDto;
                    notificationId?: string;
                  };

                  // A recipient answered by text. Only the sender receives
                  // this, and only to refresh an open delivery report.
                  if (data.type === 'notification_response') {
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationDeliveries', data.notificationId],
                    });
                  }

                  // A provider confirmed or rejected a message. Arrives on a
                  // webhook minutes after the send, with no request of its own
                  // to ride back on, so without this the dashboard could only
                  // find out by polling.
                  if (data.type === 'delivery_status') {
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationDeliveries', data.notificationId],
                    });
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationSend', data.notificationId],
                    });
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationSends', groupGuidRef.current],
                    });
                  }

                  if (data.type === 'new_notification') {
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['userNotifications', groupGuidRef.current],
                    });

                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationsByGroup', groupGuidRef.current],
                    });

                    // A re-notify arrives as a new notification, so an open
                    // delivery report should pick it up without waiting to poll.
                    queryClientRef.current.invalidateQueries({
                      queryKey: ['notificationDeliveries'],
                    });

                    const created = data.notification;
                    if (
                      created?.priority === NotificationPriority.CRITICAL ||
                      created?.priority ===
                        NotificationPriority.STAND_DOWN_SAFETY
                    ) {
                      toast.error(
                        `STAND DOWN SAFETY ALERT RECEIVED: ${created.metadata.appMessage || created.metadata.emailMessage}`
                      );
                    }
                  }
                } catch (err) {
                  console.error('[SSE] Parse error:', err);
                }
              }
            }
          }
        }
      } catch (err) {
        console.log(
          '[SSE] Error caught:',
          (err as Error).name,
          (err as Error).message
        );
        setIsConnected(false);

        if ((err as Error).name !== 'AbortError') {
          console.error('[SSE] Connection error:', err);
          scheduleReconnect();
        }
      }
    }

    connectSSE();

    return () => {
      isCleaningUp = true;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      abortController.abort();
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStorage['accessToken']]);

  return { notifications, isConnected };
};
